import React, { useState, useEffect } from "react";
import TimeSlot from "@/components/Interview/TimeSlot";
import InterviewerModal from "@/components/Interview/InterviewerModal";
import CalendlyModal from "@/components/Interview/ClandalyModal";
import { Calendar, CalendarRange } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];

const Interview = ({
  eventsData,
  recruiters,
  assignEvent,
  selectedDate: propSelectedDate,
}) => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [viewType, setViewType] = useState(() => {
    const savedViewType = localStorage.getItem("interviewViewType");
    return savedViewType || "week";
  });
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    localStorage.setItem("interviewViewType", viewType);
  }, [viewType]);

  useEffect(() => {
    if (propSelectedDate) {
      setSelectedDate(new Date(propSelectedDate));
    }
  }, [propSelectedDate]);

  useEffect(() => {
    const generateSlots = async () => {
      const slots = await generateTimeSlots(eventsData);
      setTimeSlots(slots);
      // Update selected date based on first available slot
      if (slots.length > 0 && slots[0].times.length > 0) {
        const firstSlot = slots[0].times[0];
        if (
          firstSlot.times.length > 0 &&
          firstSlot.times[0].meta?.event?.scheduled_start_time
        ) {
          setSelectedDate(
            new Date(firstSlot.times[0].meta.event.scheduled_start_time),
          );
        }
      }
    };
    generateSlots();
  }, [eventsData]);

  const getTimesAndData = async (eventsData) => {
    let flattened = {};
    eventsData?.forEach((event) => {
      Object.keys(event.slots).forEach((date) => {
        if (flattened[date]) {
          flattened[date] = flattened[date].concat(event.slots[date]);
        } else {
          flattened[date] = event.slots[date];
        }
      });
    });
    // console.log("flattened", flattened);
    // console.log("flattened eventsData", flattened);
    // const slotsData = eventsData?.slots || {};
    const slotsData = flattened || {};
    const result = [];

    // Process each date in the slots data
    Object.keys(slotsData).forEach((dateStr) => {
      const date = new Date(dateStr);
      const dayOfWeek = date
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase();

      slotsData[dateStr].forEach((slot) => {
        if (
          slot.status === "available" ||
          slot?.scheduled_start_time !== null
        ) {
          const startTime = new Date(
            slot.start_time ?? slot.scheduled_start_time,
          );
          const timeString = startTime.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          result.push({
            day: dayOfWeek,
            time: timeString,
            date: new Date(startTime),
            isBooked: slot?.scheduled_start_time !== null,
            event: slot?.scheduled_start_time !== null ? slot : null,
            scheduling_url:
              slot?.scheduled_start_time !== null
                ? slot?.payload?.scheduling_url
                : null,
            candidate: slot?.scheduled_start_time !== null ? slot?.user : null,
            interviewer:
              slot?.scheduled_start_time !== null ? slot?.recruiter : null,
          });
        }
      });
    });

    // Format the processed data
    const formattedProcessedDays = result.reduce((acc, day) => {
      const existingDay = acc.find((d) => d.day === day.day);
      if (existingDay) {
        const existingTime = existingDay.times.find((t) => t.time === day.time);
        if (!existingTime) {
          existingDay.times.push({
            time: day.time,
            meta: {
              isBooked: day.isBooked,
              event: day.event,
              scheduling_url: day.scheduling_url,
              candidate: day.candidate,
              interviewer: day.interviewer,
            },
          });
        }
      } else {
        acc.push({
          day: day.day,
          date: day.date,
          times: [
            {
              time: day.time,
              meta: {
                isBooked: day.isBooked,
                event: day.event,
                scheduling_url: day.scheduling_url,
                candidate: day.candidate,
                interviewer: day.interviewer,
              },
            },
          ],
        });
      }
      return acc;
    }, []);

    // Ensure all days are included, even if no slots
    const allDays = DAYS.map((day) => {
      const existingDay = formattedProcessedDays.find((d) => d.day === day);
      return existingDay || { day, date: null, times: [] };
    });

    return allDays;
  };

  const generateTimeSlots = async (eventsData) => {
    const processedDays = await getTimesAndData(eventsData);
    return processedDays
      .filter((day) => DAYS.includes(day.day))
      .sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day))
      .map((day) => ({
        day: day.day,
        times: day.times.sort((a, b) => a.time.localeCompare(b.time)),
      }));
  };

  const getBookedSlots = () => {
    console.log("timeSlots", timeSlots);

    return timeSlots
      .flatMap((day) =>
        day.times
          .filter((time) => time.meta.isBooked)
          .map((time) => ({
            day: day.day,
            time: time.time,
            meta: time.meta,
          })),
      )
      .sort((a, b) => {
        const timeA = parseInt(a.time.replace(":", ""));
        const timeB = parseInt(b.time.replace(":", ""));
        const dayOrder = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
        return dayOrder === 0 ? timeA - timeB : dayOrder;
      });
  };

  const filterSlotsByView = () => {
    const currentDate = new Date();
    const currentDay = currentDate.getDay() || 7;
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() - currentDay + 1);

    return timeSlots.filter((day) => {
      if (viewType === "all") return true;

      const slotDate = new Date(monday);
      slotDate.setDate(monday.getDate() + DAYS.indexOf(day.day));

      if (viewType === "week") {
        const weekEnd = new Date(monday);
        weekEnd.setDate(monday.getDate() + 4);
        return slotDate >= monday && slotDate <= weekEnd;
      }
      return true;
    });
  };

  const handleTimeSlotClick = (dayIndex, timeIndex, event) => {
    const slot = timeSlots[dayIndex].times[timeIndex];
    setSelectedSlotIndex({ dayIndex, timeIndex, event });
    setIsModalOpen(true);
  };

  const handleInterviewerSelect = (interviewerId, eventId) => {
    if (selectedSlotIndex !== null) {
      const interviewer = recruiters.find((i) => i.id === interviewerId);
      const newTimeSlots = [...timeSlots];
      newTimeSlots[selectedSlotIndex.dayIndex].times[
        selectedSlotIndex.timeIndex
      ] = {
        ...newTimeSlots[selectedSlotIndex.dayIndex].times[
          selectedSlotIndex.timeIndex
        ],
        meta: {
          ...newTimeSlots[selectedSlotIndex.dayIndex].times[
            selectedSlotIndex.timeIndex
          ].meta,
          interviewer,
        },
      };
      setTimeSlots(newTimeSlots);

      // Call assignEvent with recruiter ID and event ID
      if (selectedSlotIndex.event?.id && interviewer?.id) {
        assignEvent(selectedSlotIndex.event.id, interviewer.id);
      }
    } else {
      console.log("No selected slot index");
    }
  };

  return (
    <div className="min-h-screen  py-4 sm:py-6 md:py-8">
      <div className="w-full mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 text-primary-dark">
          {/* <h1 className="text-2xl font-bold text-foreground">Interview Time Slots</h1> */}
          <div className="flex gap-2 bg-card rounded-lg p-1 shadow-sm border border-border ml-auto">
            <button
              onClick={() => setViewType("week")}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewType === "week"
                  ? "bg-primary-dark text-white hover:bg-primary-dark/90"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <CalendarRange className="w-4 h-4" />
              <span>Week</span>
            </button>
            <button
              onClick={() => setViewType("all")}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewType === "all"
                  ? "bg-primary-dark text-white hover:bg-primary-dark/90"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>All</span>
            </button>
          </div>
        </div>
        {viewType === "all" ? (
          <div className="bg-card rounded-xl shadow-sm border border-border divide-y divide-border">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 sm:mb-6">
                {selectedDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>
              {(() => {
                const bookedSlots = getBookedSlots();
                const hasAnySlotsWithCandidates = bookedSlots.some(
                  (slot) => slot.meta.candidate,
                );

                if (!hasAnySlotsWithCandidates) {
                  return (
                    <div className="p-3 bg-muted rounded-lg text-muted-foreground text-sm">
                      No interview slots available yet.
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {DAYS.map((day) => {
                      const daySlots = bookedSlots.filter(
                        (slot) => slot.day === day,
                      );
                      if (
                        daySlots.length === 0 ||
                        !daySlots.some((slot) => slot.meta.candidate)
                      ) {
                        return null;
                      }
                      return (
                        <div key={day} className="space-y-3">
                          <h3 className="font-medium text-foreground capitalize">
                            {day}
                          </h3>
                          <div className="space-y-2">
                            {daySlots.map((slot, index) => {
                              if (
                                slot.meta.candidate ||
                                slot.meta.interviewer
                              ) {
                                return (
                                  <div
                                    key={index}
                                    onClick={() => {
                                      const dayIndex = timeSlots.findIndex(
                                        (d) => d.day === slot.day,
                                      );
                                      const timeIndex = timeSlots[
                                        dayIndex
                                      ].times.findIndex(
                                        (t) => t.time === slot.time,
                                      );
                                      handleTimeSlotClick(
                                        dayIndex,
                                        timeIndex,
                                        slot.meta.event,
                                      );
                                    }}
                                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted cursor-pointer transition-colors border border-border"
                                  >
                                    <div className="flex items-center space-x-2 sm:space-x-4">
                                      <span className="text-foreground">
                                        {slot.time}
                                      </span>
                                      <span className="text-muted-foreground">
                                        {slot.meta.candidate?.name
                                          ? `${slot.meta.candidate.name} (${slot.meta.candidate.email})`
                                          : slot.meta.candidate?.email ||
                                            (index === 0 ? "No Candidate" : "")}
                                      </span>
                                    </div>
                                    {slot.meta.interviewer ? (
                                      <span className="text-xs sm:text-sm px-2 sm:px-3 py-1 bg-green-50 text-green-600 rounded-full whitespace-nowrap">
                                        {slot.meta.interviewer?.name ||
                                          slot.meta.interviewer?.email}
                                      </span>
                                    ) : (
                                      <span className="text-xs sm:text-sm px-2 sm:px-3 py-1 bg-red-50 text-red-500 rounded-full whitespace-nowrap">
                                        Needs Recruiter
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto pb-6">
            <div className="min-w-[800px] grid grid-cols-1 md:grid-cols-5 gap-2 sm:gap-4">
              {DAYS.map((day) => {
                const daytime = timeSlots.find((d) => d.day === day) || {
                  day,
                  times: [],
                };
                if (daytime.times.length === 0) {
                  return null;
                }
                return (
                  <div key={daytime.day}>
                    <div className="text-center font-semibold text-foreground text-sm sm:text-base capitalize">
                      {daytime.day}
                    </div>
                    <div className="flex flex-col gap-2 my-2">
                      {daytime.times.map((time, timeIndex) => (
                        <div
                          key={`${daytime.day}-${time.time}`}
                          className="bg-muted p-2 rounded-lg text-center"
                        >
                          <span className="w-full block">{time.time}</span>
                          <TimeSlot
                            classes="w-full"
                            time={time}
                            candidate={time.meta.candidate}
                            interviewer={time.meta.interviewer}
                            colorClasses={
                              time.meta.interviewer
                                ? "bg-primary-dark text-white"
                                : "bg-card"
                            }
                            onClick={() =>
                              handleTimeSlotClick(
                                timeSlots.findIndex(
                                  (d) => d.day === daytime.day,
                                ),
                                timeIndex,
                                time.meta.event,
                              )
                            }
                            event={time.meta.event}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {timeSlots.every((day) => day.times.length === 0) && (
                <div className="col-span-5 p-3 bg-muted rounded-lg text-muted-foreground text-sm text-center">
                  No interview slots available yet.
                </div>
              )}
            </div>
          </div>
        )}

        <InterviewerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleInterviewerSelect}
          recruiters={recruiters}
          event={selectedSlotIndex?.event}
        />

        {selectedSlotIndex !== null &&
          timeSlots[selectedSlotIndex.dayIndex]?.times[
            selectedSlotIndex.timeIndex
          ]?.meta?.interviewer &&
          selectedSlotIndex.event && (
            <CalendlyModal
              isOpen={isCalendlyModalOpen}
              onClose={() => setIsCalendlyModalOpen(false)}
              interviewer={
                timeSlots[selectedSlotIndex.dayIndex].times[
                  selectedSlotIndex.timeIndex
                ].meta.interviewer
              }
              time={
                timeSlots[selectedSlotIndex.dayIndex].times[
                  selectedSlotIndex.timeIndex
                ].time
              }
              day={timeSlots[selectedSlotIndex.dayIndex].day}
              event={selectedSlotIndex.event}
            />
          )}
      </div>
    </div>
  );
};

export default Interview;
